import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addGroupUser,
  createGroup,
  deleteGroup,
  fetchGroups,
  fetchGroupUsers,
  removeGroupUser,
  updateGroup,
} from "@/api/groups";

export function useGroups(organizationId: number) {
  return useQuery({
    queryKey: ["groups", organizationId],
    queryFn: () => fetchGroups(organizationId),
    // 0 is "no organization picked yet", not an id. Asking for it returns 500 from scopeGuard —
    // correctly, since no group is in scope — so every render of a form before its org select is
    // answered fires a failing request and logs a server error.
    enabled: organizationId > 0,
  });
}

export function useCreateGroup(organizationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { parentId: number; name: string; creditPurchaseAllowed: boolean }) =>
      createGroup({ organizationId, ...input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["groups", organizationId] }),
  });
}

export function useUpdateGroup(organizationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number;
      patch: { name?: string; creditPurchaseAllowed?: boolean };
    }) => updateGroup(id, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["groups", organizationId] }),
  });
}

export function useDeleteGroup(organizationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteGroup(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["groups", organizationId] }),
  });
}

export function useGroupUsers(groupId: number | null) {
  return useQuery({
    queryKey: ["groupUsers", groupId],
    queryFn: () => fetchGroupUsers(groupId as number),
    enabled: groupId != null,
  });
}

/** Add a user to a group (or change their group role) — also refreshes the tree count. */
export function useAddGroupUser(organizationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId, role }: { groupId: number; userId: number; role: string }) =>
      addGroupUser(groupId, userId, role),
    onSuccess: (_data, v) => {
      queryClient.invalidateQueries({ queryKey: ["groupUsers", v.groupId] });
      queryClient.invalidateQueries({ queryKey: ["groups", organizationId] });
    },
  });
}

export function useRemoveGroupUser(organizationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: number; userId: number }) =>
      removeGroupUser(groupId, userId),
    onSuccess: (_data, v) => {
      queryClient.invalidateQueries({ queryKey: ["groupUsers", v.groupId] });
      queryClient.invalidateQueries({ queryKey: ["groups", organizationId] });
    },
  });
}
